import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import { useState } from "react";

import { GptLiveVoiceControl } from "./GptLiveVoiceControl";
import { createEmptyChatConversation } from "../../services/collaboration";
import { loadCreatedGptLiveConversationAndRefreshInBackground } from "../../utils/gptLiveConversation";
import type {
  GptLiveVoiceCallbacks,
  GptLiveVoiceConfig,
  GptLiveVoiceTransport,
} from "../../services/gptLiveVoiceTransport";
import { logConversationSseFrames } from "../../utils/idaChatStreamConfig";

function installMicrophone() {
  const track = { enabled: true, stop: jest.fn() };
  const stream = {
    getTracks: () => [track],
    getAudioTracks: () => [track],
  } as unknown as MediaStream;
  const getUserMedia = jest.fn().mockResolvedValue(stream);
  Object.defineProperty(navigator, "mediaDevices", {
    configurable: true,
    value: { getUserMedia },
  });
  return { track, getUserMedia };
}

const voiceConfig = {
  workspaceId: "workspace",
  conversationId: 42,
  authFetch: jest.fn(),
};

function makeTransport(
  overrides: Partial<GptLiveVoiceTransport> = {},
): GptLiveVoiceTransport {
  return {
    connect: jest.fn().mockResolvedValue(undefined),
    completeDelegation: jest.fn(),
    end: jest.fn().mockResolvedValue(undefined),
    ...overrides,
  };
}

async function deliverConversationFrame(
  frame: Record<string, unknown>,
  conversationId = "42",
) {
  const bytes = new TextEncoder().encode(`data: ${JSON.stringify(frame)}\n\n`);
  let consumed = false;
  const response = {
    body: {
      getReader: () => ({
        read: async () => {
          if (consumed) return { done: true, value: undefined };
          consumed = true;
          return { done: false, value: bytes };
        },
        releaseLock: jest.fn(),
      }),
    },
  } as unknown as Response;
  await act(async () => {
    await logConversationSseFrames(response, conversationId, "subscription");
  });
}

function makeDelegatingTransport() {
  let callbacks: GptLiveVoiceCallbacks | undefined;
  const pendingCalls = new Set<string>();
  const transport = makeTransport({
    connect: jest.fn(async (_stream, nextCallbacks) => {
      callbacks = nextCallbacks;
    }),
    completeDelegation: jest.fn((delegationId: string) => {
      if (!callbacks) return;
      pendingCalls.delete(delegationId);
      callbacks.onToolCallComplete?.(delegationId);
      if (pendingCalls.size === 0) callbacks.onPhase("listening");
    }),
  });
  const connect = transport.connect as jest.Mock;
  connect.mockImplementation(async (_stream, nextCallbacks) => {
    callbacks = nextCallbacks;
    const activeCallbacks = nextCallbacks as GptLiveVoiceCallbacks;
    const onDelegationStarted = activeCallbacks.onDelegationStarted;
    activeCallbacks.onDelegationStarted = (callId) => {
      pendingCalls.add(callId);
      onDelegationStarted?.(callId);
    };
  });
  return { callbacks: () => callbacks, transport };
}

async function startDelegatingVoice(
  callbacks: () => GptLiveVoiceCallbacks | undefined,
) {
  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );
  await waitFor(() => expect(callbacks()).toBeDefined());
  act(() => {
    callbacks()?.onToolCallStart?.("voice-call-1");
    callbacks()?.onDelegationStarted?.("voice-call-1");
    callbacks()?.onPhase("delegating");
  });
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "IDA is working",
  );
}

test("matching done turn clears the delegated working phase", async () => {
  installMicrophone();
  const { callbacks, transport } = makeDelegatingTransport();
  render(
    <GptLiveVoiceControl {...voiceConfig} transportFactory={() => transport} />,
  );
  await startDelegatingVoice(callbacks);
  act(() =>
    callbacks()?.onDelegationAccepted?.("voice-call-1", "submission-1"),
  );
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "IDA is working",
  );

  await deliverConversationFrame({
    type: "assistant_turn_started",
    conversation_id: 42,
    submission_id: "submission-1",
    voice_call_id: "voice-call-1",
    assistant_turn_id: "turn-1",
  });
  await deliverConversationFrame({
    type: "done",
    conversation_id: 42,
    assistant_turn_id: "turn-1",
  });

  expect(transport.completeDelegation).toHaveBeenCalledWith("voice-call-1");
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "Listening",
  );
});

test("done received before delegation acceptance clears by submission id", async () => {
  installMicrophone();
  const { callbacks, transport } = makeDelegatingTransport();
  render(
    <GptLiveVoiceControl {...voiceConfig} transportFactory={() => transport} />,
  );
  await startDelegatingVoice(callbacks);

  await deliverConversationFrame({
    type: "done",
    conversation_id: 42,
    submission_id: "submission-fast",
  });
  expect(transport.completeDelegation).not.toHaveBeenCalled();
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "IDA is working",
  );

  act(() =>
    callbacks()?.onDelegationAccepted?.("voice-call-1", "submission-fast"),
  );

  expect(transport.completeDelegation).toHaveBeenCalledWith("voice-call-1");
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "Listening",
  );
});

test("early terminal correlation keeps only the most recent twenty ids", async () => {
  installMicrophone();
  const { callbacks, transport } = makeDelegatingTransport();
  render(
    <GptLiveVoiceControl {...voiceConfig} transportFactory={() => transport} />,
  );

  for (let index = 0; index < 25; index += 1) {
    await deliverConversationFrame({
      type: "done",
      conversation_id: 42,
      submission_id: `submission-${index}`,
      assistant_turn_id: `turn-${index}`,
    });
  }
  await deliverConversationFrame({
    type: "assistant_turn_started",
    conversation_id: 42,
    submission_id: "submission-0",
    assistant_turn_id: "turn-0",
  });

  await startDelegatingVoice(callbacks);
  act(() =>
    callbacks()?.onDelegationAccepted?.("voice-call-1", "submission-0"),
  );

  expect(transport.completeDelegation).not.toHaveBeenCalled();
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "IDA is working",
  );
});

async function startSecondDelegation(
  callbacks: () => GptLiveVoiceCallbacks | undefined,
) {
  act(() => {
    callbacks()?.onToolCallStart?.("voice-call-2");
    callbacks()?.onDelegationStarted?.("voice-call-2");
    callbacks()?.onPhase("delegating");
  });
  act(() =>
    callbacks()?.onDelegationAccepted?.("voice-call-2", "submission-2"),
  );
  await deliverConversationFrame({
    type: "assistant_turn_started",
    conversation_id: 42,
    submission_id: "submission-2",
    voice_call_id: "voice-call-2",
    assistant_turn_id: "turn-2",
  });
}

async function startFirstDelegationTurn(
  callbacks: () => GptLiveVoiceCallbacks | undefined,
) {
  act(() =>
    callbacks()?.onDelegationAccepted?.("voice-call-1", "submission-1"),
  );
  await deliverConversationFrame({
    type: "assistant_turn_started",
    conversation_id: 42,
    submission_id: "submission-1",
    voice_call_id: "voice-call-1",
    assistant_turn_id: "turn-1",
  });
}

test.each([
  ["first", "second", "turn-1", "voice-call-1"],
  ["second", "first", "turn-2", "voice-call-2"],
] as const)(
  "overlapping delegations remain working after the %s terminal and listen after the %s terminal",
  async (_first, _second, firstTurnId, firstCallId) => {
    installMicrophone();
    const { callbacks, transport } = makeDelegatingTransport();
    render(
      <GptLiveVoiceControl
        {...voiceConfig}
        transportFactory={() => transport}
      />,
    );
    await startDelegatingVoice(callbacks);
    await startFirstDelegationTurn(callbacks);
    await startSecondDelegation(callbacks);

    await deliverConversationFrame({
      type: "done",
      conversation_id: 42,
      assistant_turn_id: firstTurnId,
    });
    expect(transport.completeDelegation).toHaveBeenCalledWith(firstCallId);
    expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
      "IDA is working",
    );

    const secondTurnId = firstTurnId === "turn-1" ? "turn-2" : "turn-1";
    const secondCallId =
      firstCallId === "voice-call-1" ? "voice-call-2" : "voice-call-1";
    await deliverConversationFrame({
      type: "done",
      conversation_id: 42,
      assistant_turn_id: secondTurnId,
    });
    expect(transport.completeDelegation).toHaveBeenCalledWith(secondCallId);
    expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
      "Listening",
    );
  },
);

test("one overlapping delegation terminal leaves the other working", async () => {
  installMicrophone();
  const { callbacks, transport } = makeDelegatingTransport();
  render(
    <GptLiveVoiceControl {...voiceConfig} transportFactory={() => transport} />,
  );
  await startDelegatingVoice(callbacks);
  await startFirstDelegationTurn(callbacks);
  await startSecondDelegation(callbacks);

  await deliverConversationFrame({
    type: "done",
    conversation_id: 42,
    assistant_turn_id: "turn-1",
  });
  act(() => callbacks()?.onPhase("listening"));

  expect(transport.completeDelegation).toHaveBeenCalledTimes(1);
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "IDA is working",
  );
});

test("failure removes only its matching overlapping delegation", async () => {
  installMicrophone();
  const { callbacks, transport } = makeDelegatingTransport();
  render(
    <GptLiveVoiceControl {...voiceConfig} transportFactory={() => transport} />,
  );
  await startDelegatingVoice(callbacks);
  await startFirstDelegationTurn(callbacks);
  await startSecondDelegation(callbacks);

  await deliverConversationFrame({
    type: "queue_item_failed",
    conversation_id: 42,
    submission_id: "submission-1",
  });
  expect(transport.completeDelegation).toHaveBeenCalledWith("voice-call-1");
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "IDA is working",
  );

  await deliverConversationFrame({
    type: "done",
    conversation_id: 42,
    assistant_turn_id: "turn-2",
  });
  expect(transport.completeDelegation).toHaveBeenCalledWith("voice-call-2");
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "Listening",
  );
});

test("matching submission error clears the delegated working phase", async () => {
  installMicrophone();
  const { callbacks, transport } = makeDelegatingTransport();
  render(
    <GptLiveVoiceControl {...voiceConfig} transportFactory={() => transport} />,
  );
  await startDelegatingVoice(callbacks);
  act(() =>
    callbacks()?.onDelegationAccepted?.("voice-call-1", "submission-error"),
  );

  await deliverConversationFrame({
    type: "error",
    conversation_id: 42,
    submission_id: "submission-error",
  });

  expect(transport.completeDelegation).toHaveBeenCalledWith("voice-call-1");
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "Listening",
  );
});

test("matching terminal cancellation clears by assistant turn ID", async () => {
  installMicrophone();
  const { callbacks, transport } = makeDelegatingTransport();
  render(
    <GptLiveVoiceControl {...voiceConfig} transportFactory={() => transport} />,
  );
  await startDelegatingVoice(callbacks);
  act(() =>
    callbacks()?.onDelegationAccepted?.("voice-call-1", "submission-cancel"),
  );

  await deliverConversationFrame({
    type: "assistant_turn_started",
    conversation_id: 42,
    submission_id: "submission-cancel",
    voice_call_id: "voice-call-1",
    assistant_turn_id: "turn-cancel",
  });
  await deliverConversationFrame({
    type: "queue_item_cancelled",
    conversation_id: 42,
    assistant_turn_id: "turn-cancel",
  });

  expect(transport.completeDelegation).toHaveBeenCalledWith("voice-call-1");
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "Listening",
  );
});

test("unrelated answer and error frames do not clear a delegated phase", async () => {
  installMicrophone();
  const { callbacks, transport } = makeDelegatingTransport();
  render(
    <GptLiveVoiceControl {...voiceConfig} transportFactory={() => transport} />,
  );
  await startDelegatingVoice(callbacks);
  act(() =>
    callbacks()?.onDelegationAccepted?.("voice-call-1", "submission-expected"),
  );

  await deliverConversationFrame({
    type: "assistant_turn_started",
    conversation_id: 42,
    submission_id: "submission-expected",
    voice_call_id: "voice-call-1",
    assistant_turn_id: "turn-expected",
  });
  await deliverConversationFrame({
    type: "done",
    conversation_id: 42,
    assistant_turn_id: "turn-other",
  });
  await deliverConversationFrame(
    {
      type: "error",
      conversation_id: 43,
      submission_id: "submission-expected",
    },
    "43",
  );

  expect(transport.completeDelegation).not.toHaveBeenCalled();
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "IDA is working",
  );
});

test("matching answer frames received before the provider call id clear when it arrives", async () => {
  installMicrophone();
  const { callbacks, transport } = makeDelegatingTransport();
  render(
    <GptLiveVoiceControl {...voiceConfig} transportFactory={() => transport} />,
  );
  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );
  await waitFor(() => expect(callbacks()).toBeDefined());

  await deliverConversationFrame({
    type: "assistant_turn_started",
    conversation_id: 42,
    submission_id: "submission-fast",
    voice_call_id: "voice-call-1",
    assistant_turn_id: "turn-fast",
  });
  await deliverConversationFrame({
    type: "done",
    conversation_id: 42,
    assistant_turn_id: "turn-fast",
  });
  expect(transport.completeDelegation).not.toHaveBeenCalled();
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "Listening",
  );

  act(() => {
    callbacks()?.onToolCallStart?.("voice-call-1");
    callbacks()?.onDelegationStarted?.("voice-call-1");
    callbacks()?.onPhase("delegating");
  });

  expect(transport.completeDelegation).toHaveBeenCalledWith("voice-call-1");
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "Listening",
  );
});

test.each(["end", "error", "disconnect"] as const)(
  "does not accept a late delegated-answer frame after voice %s",
  async (resetPath) => {
    installMicrophone();
    const { callbacks, transport } = makeDelegatingTransport();
    render(
      <GptLiveVoiceControl
        {...voiceConfig}
        transportFactory={() => transport}
      />,
    );
    await startDelegatingVoice(callbacks);
    act(() =>
      callbacks()?.onDelegationAccepted?.("voice-call-1", "submission-stale"),
    );

    if (resetPath === "end") {
      fireEvent.click(
        screen.getByRole("button", { name: "End voice conversation" }),
      );
    } else if (resetPath === "error") {
      act(() => callbacks()?.onDelegationFailure?.("provider_error"));
    } else {
      act(() => callbacks()?.onDisconnect?.(null, false));
    }

    await deliverConversationFrame({
      type: "assistant_turn_started",
      conversation_id: 42,
      submission_id: "submission-stale",
      voice_call_id: "voice-call-1",
      assistant_turn_id: "turn-stale",
    });
    await deliverConversationFrame({
      type: "done",
      conversation_id: 42,
      assistant_turn_id: "turn-stale",
    });

    expect(transport.completeDelegation).not.toHaveBeenCalled();
  },
);

test("starts continuous microphone capture from the composer wave button", async () => {
  const { track, getUserMedia } = installMicrophone();
  const transport = makeTransport();
  const transportFactory = jest.fn((_config: GptLiveVoiceConfig) => transport);
  render(
    <GptLiveVoiceControl
      {...voiceConfig}
      transportFactory={transportFactory}
    />,
  );

  const startButton = screen.getByRole("button", {
    name: "Start voice conversation",
  });
  expect(startButton).toHaveAttribute("aria-pressed", "false");
  expect(screen.getByTestId("gpt-live-wave-static")).toBeInTheDocument();
  fireEvent.click(startButton);

  await waitFor(() =>
    expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
      "Listening",
    ),
  );
  expect(getUserMedia).toHaveBeenCalledWith({ audio: true });
  expect(track.enabled).toBe(true);
  expect(transport.connect).toHaveBeenCalledTimes(1);
  expect(screen.getByTestId("gpt-live-wave-button")).toHaveAttribute(
    "aria-pressed",
    "true",
  );
  expect(screen.getByTestId("gpt-live-wave-button")).toHaveClass(
    "MuiIconButton-variantSolid",
  );
  expect(screen.getByTestId("gpt-live-wave-animation")).toBeInTheDocument();

  fireEvent.click(
    screen.getByRole("button", { name: "End voice conversation" }),
  );
  expect(
    await screen.findByRole("button", { name: "Start voice conversation" }),
  ).toBeInTheDocument();
  expect(transport.end).toHaveBeenCalledTimes(1);
  expect(track.stop).toHaveBeenCalledTimes(1);
});

test("forwards realtime function-call start to the shared composing callback", async () => {
  installMicrophone();
  const onToolComposing = jest.fn();
  const connect = jest.fn(
    async (_stream: MediaStream, callbacks: GptLiveVoiceCallbacks) => {
      callbacks.onToolCallStart?.("voice-call-1");
    },
  );
  render(
    <GptLiveVoiceControl
      {...voiceConfig}
      onToolComposing={onToolComposing}
      transportFactory={() => makeTransport({ connect })}
    />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );

  await waitFor(() =>
    expect(onToolComposing).toHaveBeenCalledWith("voice-call-1"),
  );
});

test("clears shared composition when the matching realtime tool call completes", async () => {
  installMicrophone();
  const onToolComposing = jest.fn();
  const connect = jest.fn(
    async (_stream: MediaStream, callbacks: GptLiveVoiceCallbacks) => {
      callbacks.onToolCallStart?.("voice-call-1");
      callbacks.onToolCallComplete?.("voice-call-1");
    },
  );
  render(
    <GptLiveVoiceControl
      {...voiceConfig}
      onToolComposing={onToolComposing}
      transportFactory={() => makeTransport({ connect })}
    />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );

  await waitFor(() =>
    expect(onToolComposing.mock.calls.slice(-2)).toEqual([
      ["voice-call-1"],
      [null],
    ]),
  );
});

test("clears shared composition on delegation failure", async () => {
  installMicrophone();
  const onToolComposing = jest.fn();
  const connect = jest.fn(
    async (_stream: MediaStream, callbacks: GptLiveVoiceCallbacks) => {
      callbacks.onToolCallStart?.("voice-call-1");
      callbacks.onDelegationFailure?.("provider_error");
    },
  );
  render(
    <GptLiveVoiceControl
      {...voiceConfig}
      onToolComposing={onToolComposing}
      transportFactory={() => makeTransport({ connect })}
    />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );

  await waitFor(() =>
    expect(onToolComposing.mock.calls.slice(-2)).toEqual([
      ["voice-call-1"],
      [null],
    ]),
  );
  expect(
    screen.getByText(/could not complete the request/i),
  ).toBeInTheDocument();
});

test("uses the plain unbordered variant while voice is idle", () => {
  render(<GptLiveVoiceControl {...voiceConfig} transportFactory={jest.fn()} />);
  expect(screen.getByTestId("gpt-live-wave-button")).toHaveClass(
    "MuiIconButton-variantPlain",
  );
});

test("empty-chat voice creates one conversation before starting its session", async () => {
  installMicrophone();
  const authFetch = jest.fn().mockResolvedValue({ conversation_id: 314 });
  const transport = makeTransport();
  const transportFactory = jest.fn((_config: GptLiveVoiceConfig) => transport);
  render(
    <GptLiveVoiceControl
      workspaceId="workspace"
      conversationId={null}
      ensureConversation={() =>
        createEmptyChatConversation(authFetch, "workspace")
      }
      authFetch={authFetch}
      transportFactory={transportFactory}
    />,
  );

  expect(screen.getByTestId("gpt-live-wave-button")).toBeInTheDocument();
  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );

  await waitFor(() => expect(transport.connect).toHaveBeenCalledTimes(1));
  expect(authFetch).toHaveBeenCalledTimes(1);
  expect(authFetch).toHaveBeenCalledWith(
    "/api/workspace/ida/chat/conversations",
    "create chat conversation",
    {
      method: "POST",
      headers: { "X-Workspace-Id": "workspace" },
    },
  );
  expect(transportFactory).toHaveBeenCalledTimes(1);
  expect(transportFactory.mock.calls[0][0].conversationId).toBe(314);
});

test("empty-chat voice starts before the conversation list refresh resolves", async () => {
  const { getUserMedia } = installMicrophone();
  let resolveListRefresh: (() => void) | undefined;
  const listRefresh = new Promise<void>((resolve) => {
    resolveListRefresh = resolve;
  });
  let selectedConversationId: number | null = null;
  const transport = makeTransport();
  const transportFactory = jest.fn((_config: GptLiveVoiceConfig) => transport);
  const refreshConversationList = jest.fn(() => listRefresh);
  render(
    <GptLiveVoiceControl
      workspaceId="workspace"
      conversationId={null}
      ensureConversation={() =>
        loadCreatedGptLiveConversationAndRefreshInBackground(
          314,
          () => selectedConversationId,
          async (id) => {
            selectedConversationId = id;
          },
          refreshConversationList,
          jest.fn(),
        )
      }
      authFetch={jest.fn()}
      transportFactory={transportFactory}
    />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );

  await waitFor(() => expect(transport.connect).toHaveBeenCalledTimes(1));
  expect(getUserMedia).toHaveBeenCalledWith({ audio: true });
  expect(transportFactory.mock.calls[0][0].conversationId).toBe(314);
  expect(refreshConversationList).toHaveBeenCalledTimes(1);

  await act(async () => {
    resolveListRefresh?.();
    await listRefresh;
  });
});

test("empty-chat selection keeps the session; switching conversations ends it", async () => {
  installMicrophone();
  const transport = makeTransport();
  const transportFactory = jest.fn((_config: GptLiveVoiceConfig) => transport);
  let selectConversation: ((id: number | null) => void) | undefined;
  function ControlledEmptyChat() {
    const [conversationId, setConversationId] = useState<number | null>(null);
    selectConversation = setConversationId;
    return (
      <GptLiveVoiceControl
        workspaceId="workspace"
        conversationId={conversationId}
        ensureConversation={async () => {
          setConversationId(314);
          return 314;
        }}
        authFetch={jest.fn()}
        transportFactory={transportFactory}
      />
    );
  }

  render(<ControlledEmptyChat />);
  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );

  await waitFor(() => expect(transport.connect).toHaveBeenCalledTimes(1));
  expect(transportFactory.mock.calls[0][0].conversationId).toBe(314);
  expect(transport.end).not.toHaveBeenCalled();

  act(() => selectConversation?.(43));
  await waitFor(() => expect(transport.end).toHaveBeenCalledTimes(1));
  expect(
    await screen.findByRole("button", { name: "Start voice conversation" }),
  ).toBeInTheDocument();
});

test("switching away while empty-chat creation is pending does not open the microphone", async () => {
  const { getUserMedia } = installMicrophone();
  let resolveCreation: ((id: number) => void) | undefined;
  let selectConversation: ((id: number | null) => void) | undefined;
  const transportFactory = jest.fn();
  function PendingEmptyChat() {
    const [conversationId, setConversationId] = useState<number | null>(null);
    selectConversation = setConversationId;
    return (
      <GptLiveVoiceControl
        workspaceId="workspace"
        conversationId={conversationId}
        ensureConversation={() => {
          setConversationId(314);
          return new Promise<number>((resolve) => {
            resolveCreation = resolve;
          });
        }}
        authFetch={jest.fn()}
        transportFactory={transportFactory}
      />
    );
  }

  render(<PendingEmptyChat />);
  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );
  await waitFor(() => expect(resolveCreation).toBeDefined());
  expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
    "Connecting to voice",
  );

  act(() => selectConversation?.(43));
  expect(
    await screen.findByRole("button", { name: "Start voice conversation" }),
  ).toBeInTheDocument();

  await act(async () => {
    resolveCreation?.(314);
  });

  expect(getUserMedia).not.toHaveBeenCalled();
  expect(transportFactory).not.toHaveBeenCalled();
});

test("switching from an active conversation ends its voice transport", async () => {
  installMicrophone();
  const transport = makeTransport();
  const transportFactory = jest.fn((_config: GptLiveVoiceConfig) => transport);
  const { rerender } = render(
    <GptLiveVoiceControl
      {...voiceConfig}
      transportFactory={transportFactory}
    />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );
  await waitFor(() => expect(transport.connect).toHaveBeenCalledTimes(1));

  rerender(
    <GptLiveVoiceControl
      {...voiceConfig}
      conversationId={43}
      transportFactory={transportFactory}
    />,
  );
  await waitFor(() => expect(transport.end).toHaveBeenCalledTimes(1));
  expect(
    await screen.findByRole("button", { name: "Start voice conversation" }),
  ).toBeInTheDocument();
});

test("empty-chat creation failure is visible and does not start a voice session", async () => {
  const { getUserMedia } = installMicrophone();
  const authFetch = jest.fn().mockRejectedValue(new Error("request failed"));
  const transportFactory = jest.fn();
  render(
    <GptLiveVoiceControl
      workspaceId="workspace"
      conversationId={null}
      ensureConversation={() =>
        createEmptyChatConversation(authFetch, "workspace")
      }
      authFetch={authFetch}
      transportFactory={transportFactory}
    />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );

  expect(await screen.findByRole("alert")).toHaveTextContent(
    "Could not create a conversation. Try again.",
  );
  expect(authFetch).toHaveBeenCalledTimes(1);
  expect(getUserMedia).not.toHaveBeenCalled();
  expect(transportFactory).not.toHaveBeenCalled();
});

test("abandoned empty-chat creation stops quietly before microphone or session startup", async () => {
  const { getUserMedia } = installMicrophone();
  const authFetch = jest.fn();
  const transportFactory = jest.fn();
  render(
    <GptLiveVoiceControl
      workspaceId="workspace"
      conversationId={null}
      ensureConversation={async () => null}
      authFetch={authFetch}
      transportFactory={transportFactory}
    />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );

  expect(
    await screen.findByRole("button", { name: "Start voice conversation" }),
  ).toBeInTheDocument();
  expect(screen.queryByRole("alert")).not.toBeInTheDocument();
  expect(getUserMedia).not.toHaveBeenCalled();
  expect(authFetch).not.toHaveBeenCalled();
  expect(transportFactory).not.toHaveBeenCalled();
});

test("shows one manual reconnect action after a WebRTC drop", async () => {
  const { getUserMedia, track } = installMicrophone();
  const consoleError = jest
    .spyOn(console, "error")
    .mockImplementation(() => {});
  const callbacks: Array<
    Parameters<GptLiveVoiceTransport["connect"]>[1] | undefined
  > = [];
  const firstTransport = makeTransport({
    connect: jest.fn(async (_stream, nextCallbacks) => {
      callbacks[0] = nextCallbacks;
    }),
  });
  const secondTransport = makeTransport({
    connect: jest.fn(async (_stream, nextCallbacks) => {
      callbacks[1] = nextCallbacks;
    }),
  });
  const transportFactory = jest
    .fn()
    .mockReturnValueOnce(firstTransport)
    .mockReturnValueOnce(secondTransport);
  render(
    <GptLiveVoiceControl
      {...voiceConfig}
      transportFactory={transportFactory}
    />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );
  await waitFor(() => expect(callbacks[0]).toBeDefined());
  act(() => callbacks[0]?.onDisconnect?.(null, false));

  expect(await screen.findByRole("alert")).toHaveTextContent(
    "Voice session ended.",
  );
  expect(
    screen.getByRole("button", { name: "Reconnect voice" }),
  ).toBeInTheDocument();
  expect(transportFactory).toHaveBeenCalledTimes(1);
  expect(getUserMedia).toHaveBeenCalledTimes(1);
  expect(track.stop).toHaveBeenCalledTimes(1);
  act(() => callbacks[0]?.onDisconnect?.("expired", true));
  expect(await screen.findByRole("alert")).toHaveTextContent(
    "The voice session reached its time limit.",
  );
  act(() => callbacks[0]?.onDisconnect?.("max_session_length", true));
  expect(await screen.findByRole("alert")).toHaveTextContent(
    "Voice session reached its maximum length",
  );
  expect(
    screen.getByRole("button", { name: "Reconnect voice" }),
  ).toBeInTheDocument();
  expect(consoleError).toHaveBeenCalledWith(
    expect.any(Error),
    "Failed to maintain GPT-Live voice session for conversation 42: WebRTC peer connection was lost",
  );

  fireEvent.click(screen.getByRole("button", { name: "Reconnect voice" }));
  await waitFor(() =>
    expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
      "Listening",
    ),
  );
  expect(secondTransport.connect).toHaveBeenCalledTimes(1);
  expect(transportFactory).toHaveBeenCalledTimes(2);
  expect(getUserMedia).toHaveBeenCalledTimes(2);
  expect(transportFactory.mock.calls[1][0].conversationId).toBe(42);
  expect(consoleError).toHaveBeenCalledTimes(1);
  consoleError.mockRestore();
});

test("provider-ended signal clears voice preparation and refuses input until reconnect", async () => {
  const { getUserMedia, track } = installMicrophone();
  const callbacks: Array<
    Parameters<GptLiveVoiceTransport["connect"]>[1] | undefined
  > = [];
  const firstTransport = makeTransport({
    connect: jest.fn(async (_stream, nextCallbacks) => {
      callbacks[0] = nextCallbacks;
      nextCallbacks.onSessionCreated?.("provider-session-1");
      nextCallbacks.onToolCallStart?.("voice-call-1");
    }),
  });
  const secondTransport = makeTransport({
    connect: jest.fn(async (_stream, nextCallbacks) => {
      callbacks[1] = nextCallbacks;
      nextCallbacks.onSessionCreated?.("provider-session-2");
    }),
  });
  const transportFactory = jest
    .fn()
    .mockReturnValueOnce(firstTransport)
    .mockReturnValueOnce(secondTransport);
  const onPhaseChange = jest.fn();
  const onToolComposing = jest.fn();
  const onTranscriptTurns = jest.fn();
  render(
    <GptLiveVoiceControl
      {...voiceConfig}
      onPhaseChange={onPhaseChange}
      onTranscriptTurns={onTranscriptTurns}
      onToolComposing={onToolComposing}
      transportFactory={transportFactory}
    />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );
  await waitFor(() =>
    expect(onToolComposing).toHaveBeenCalledWith("voice-call-1"),
  );
  expect(onToolComposing.mock.calls).toContainEqual(["voice-call-1"]);
  expect(callbacks[0]?.onToolCallStart).toBeDefined();

  const frame = new TextEncoder().encode(
    'data: {"type":"voice_session_ended","conversation_id":42,"session_id":"provider-session-1","reason":"content_filter","notice_persisted":false}\n\n',
  );
  let consumed = false;
  const response = {
    body: {
      getReader: () => ({
        read: async () => {
          if (consumed) return { done: true, value: undefined };
          consumed = true;
          return { done: false, value: frame };
        },
        releaseLock: jest.fn(),
      }),
    },
  } as unknown as Response;
  await act(async () => {
    await logConversationSseFrames(response, "42", "subscription");
  });

  expect(onToolComposing).toHaveBeenLastCalledWith(null);
  expect(await screen.findByRole("alert")).toHaveTextContent(
    "The voice provider ended the session after filtering the response.",
  );
  expect(
    screen.getByRole("button", { name: "Reconnect voice" }),
  ).toBeInTheDocument();
  expect(firstTransport.end).toHaveBeenCalledWith({ notifyServer: false });
  expect(track.stop).toHaveBeenCalledTimes(1);
  expect(onToolComposing.mock.calls.slice(-2)).toEqual([
    ["voice-call-1"],
    [null],
  ]);
  expect(onPhaseChange).toHaveBeenLastCalledWith(null);

  act(() => {
    callbacks[0]?.onToolCallStart?.("late-voice-input");
    callbacks[0]?.onTranscriptTurns?.([], null);
  });
  expect(onToolComposing).not.toHaveBeenCalledWith("late-voice-input");
  expect(onTranscriptTurns).not.toHaveBeenCalled();
  expect(transportFactory).toHaveBeenCalledTimes(1);
  expect(getUserMedia).toHaveBeenCalledTimes(1);

  fireEvent.click(screen.getByRole("button", { name: "Reconnect voice" }));
  await waitFor(() => expect(secondTransport.connect).toHaveBeenCalledTimes(1));
  expect(transportFactory).toHaveBeenCalledTimes(2);
  expect(getUserMedia).toHaveBeenCalledTimes(2);
});

test("does not duplicate a provider error notice that was persisted to chat", async () => {
  installMicrophone();
  let callbacks: GptLiveVoiceCallbacks | undefined;
  const transport = makeTransport({
    connect: jest.fn(async (_stream, nextCallbacks) => {
      callbacks = nextCallbacks;
      nextCallbacks.onSessionCreated?.("provider-session-1");
    }),
  });
  render(
    <GptLiveVoiceControl {...voiceConfig} transportFactory={() => transport} />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );
  await waitFor(() => expect(callbacks).toBeDefined());

  const frame = new TextEncoder().encode(
    'data: {"type":"voice_session_ended","conversation_id":42,"session_id":"provider-session-1","reason":"provider_error","notice_persisted":true}\n\n',
  );
  let consumed = false;
  const response = {
    body: {
      getReader: () => ({
        read: async () => {
          if (consumed) return { done: true, value: undefined };
          consumed = true;
          return { done: false, value: frame };
        },
        releaseLock: jest.fn(),
      }),
    },
  } as unknown as Response;
  await act(async () => {
    await logConversationSseFrames(response, "42", "subscription");
  });

  expect(screen.queryByRole("alert")).not.toBeInTheDocument();
  expect(
    screen.getByRole("button", { name: "Reconnect voice" }),
  ).toBeInTheDocument();
  expect(transport.end).toHaveBeenCalledWith({ notifyServer: false });
});

test("ignores a late reconcile reason after the user reconnects", async () => {
  installMicrophone();
  const callbacks: Array<
    Parameters<GptLiveVoiceTransport["connect"]>[1] | undefined
  > = [];
  const firstTransport = makeTransport({
    connect: jest.fn(async (_stream, nextCallbacks) => {
      callbacks[0] = nextCallbacks;
    }),
  });
  const secondTransport = makeTransport({
    connect: jest.fn(async (_stream, nextCallbacks) => {
      callbacks[1] = nextCallbacks;
    }),
  });
  const transportFactory = jest
    .fn()
    .mockReturnValueOnce(firstTransport)
    .mockReturnValueOnce(secondTransport);
  render(
    <GptLiveVoiceControl
      {...voiceConfig}
      transportFactory={transportFactory}
    />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );
  await waitFor(() => expect(callbacks[0]).toBeDefined());
  act(() => callbacks[0]?.onDisconnect?.(null, false));
  fireEvent.click(screen.getByRole("button", { name: "Reconnect voice" }));
  await waitFor(() =>
    expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
      "Listening",
    ),
  );

  act(() => callbacks[0]?.onDisconnect?.("expired", true));
  expect(screen.queryByRole("alert")).not.toBeInTheDocument();
  expect(
    screen.getByRole("button", { name: "End voice conversation" }),
  ).toBeInTheDocument();
});

test("surfaces microphone errors inline and allows retry", async () => {
  const { track } = installMicrophone();
  const transportFactory = jest
    .fn()
    .mockReturnValueOnce(
      makeTransport({
        connect: jest.fn().mockRejectedValue(new Error("failed")),
      }),
    )
    .mockReturnValue(makeTransport());
  render(
    <GptLiveVoiceControl
      {...voiceConfig}
      transportFactory={transportFactory}
    />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );
  expect(await screen.findByRole("alert")).toHaveTextContent(
    "Voice could not connect.",
  );
  expect(track.stop).toHaveBeenCalledTimes(1);

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );
  await waitFor(() =>
    expect(screen.getByTestId("gpt-live-voice-state")).toHaveTextContent(
      "Listening",
    ),
  );
  expect(transportFactory).toHaveBeenCalledTimes(2);
});

test("hides the inline status while the full-width error feedback is visible", async () => {
  installMicrophone();
  const transport = makeTransport({
    connect: jest.fn().mockRejectedValue(new Error("failed")),
  });

  function ControlledFeedback() {
    const [feedback, setFeedback] = useState<{
      message: string;
      tone: "danger" | "neutral";
    } | null>(null);

    return (
      <>
        {feedback?.tone === "danger" && (
          <div data-testid="gpt-live-feedback-row">
            <div role="alert">{feedback.message}</div>
          </div>
        )}
        <GptLiveVoiceControl
          {...voiceConfig}
          onFeedbackChange={setFeedback}
          transportFactory={() => transport}
        />
      </>
    );
  }

  render(<ControlledFeedback />);
  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );

  expect(
    await screen.findByTestId("gpt-live-feedback-row"),
  ).toBeInTheDocument();
  expect(screen.getByRole("alert")).toHaveTextContent(
    "Voice could not connect.",
  );
  expect(screen.queryByTestId("gpt-live-voice-state")).not.toBeInTheDocument();
});

test("shows provider capacity errors beside the composer control", async () => {
  installMicrophone();
  const transport = makeTransport({
    connect: jest.fn().mockRejectedValue({ status: 429 }),
  });
  render(
    <GptLiveVoiceControl {...voiceConfig} transportFactory={() => transport} />,
  );

  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );
  expect(await screen.findByRole("alert")).toHaveTextContent(
    "GPT-Live capacity is temporarily unavailable.",
  );
  expect(
    screen.getByRole("button", { name: "Start voice conversation" }),
  ).toBeInTheDocument();
});

test("shows a visible inline message when remote audio playback is rejected", async () => {
  installMicrophone();
  let callbacks: Parameters<GptLiveVoiceTransport["connect"]>[1] | undefined;
  const transport = makeTransport({
    connect: jest.fn(async (_stream, nextCallbacks) => {
      callbacks = nextCallbacks;
    }),
  });
  render(
    <GptLiveVoiceControl {...voiceConfig} transportFactory={() => transport} />,
  );
  fireEvent.click(
    screen.getByRole("button", { name: "Start voice conversation" }),
  );
  await waitFor(() => expect(callbacks).toBeDefined());

  act(() => callbacks?.onPlaybackError?.());
  expect(await screen.findByRole("alert")).toHaveTextContent(
    "Voice audio could not play. Check your browser's audio permissions.",
  );
  expect(
    screen.getByRole("button", { name: "End voice conversation" }),
  ).toBeInTheDocument();
});
